import React, { useEffect, useMemo, useState } from "react";
import {
  Button,
  Checkbox,
  DatePicker,
  Form,
  Input,
  InputNumber,
  Radio,
  Select,
  Space,
  Steps,
  Typography,
} from "antd";
import {
  ArrowLeftOutlined,
  BankOutlined,
  EyeInvisibleOutlined,
  EyeOutlined,
  IdcardOutlined,
  LockOutlined,
  MailOutlined,
  PhoneOutlined,
  ShopOutlined,
  SafetyOutlined,
  UserOutlined,
} from "@ant-design/icons";
import OtpInput from "react-otp-input";
import { useNavigate } from "react-router-dom";
import { useTheme } from "../../store/context/theme-context";
import { useNotification } from "../../store/context/notification-context";
import { useTenant } from "../../store/context/tenant-context";
import { useFormData } from "../../store/context/form-data-context";
import { useApiRequests } from "../../utils/api/https/executor";
import { MeansOfIdentificationEnum } from "../../shared/constants/constants";
import { PhoneAreas } from "../../utils/data/jsonData";
import { navigateToAuth } from "../../utils/helper/redirect";
import LanguageSelector from "../../shared/languages/LanguageSelector";
import TenantSwitcher from "../../components/General/TenantSwitcher";

const { Title, Text, Link } = Typography;
const { Step } = Steps;
const { Option } = Select;

const GENDERS = ["Male", "Female"];

const Signup = () => {
  const [form] = Form.useForm();
  const navigate = useNavigate();
  const tenant = useTenant();
  const { theme } = useTheme();
  const { triggerNotification } = useNotification();
  const { formData: signupContext, setFormData: setSignupContext } =
    useFormData();
  const {
    preRegisterUser,
    preRegisterCorporateUser,
    validateOTP,
    resendOTP,
    completeRegistration,
    securityQuestionQuery,
  } = useApiRequests();

  const isDark = theme === "dark";
  const primary = tenant?.theme?.colorPrimary || "#1890ff";

  // ──────────────────────────────────────────────────────────────────────────
  // State
  // ──────────────────────────────────────────────────────────────────────────
  const initialAccountType = localStorage.getItem("accountType") || null;
  const [accountType, setAccountType] = useState(initialAccountType);
  // Three-stage state machine:
  //   "wizard"   — Account Type → Profile → [Company] → Contact & Identity
  //   "otp"      — standalone OTP verification (visually matches the login OTP screen)
  //   "security" — standalone security questions + password (final step)
  const [stage, setStage] = useState("wizard");
  const [currentStep, setCurrentStep] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  // Saved on successful PreRegistration → required for ValidateOtp / CompleteRegistration
  const [signupSession, setSignupSession] = useState({ userId: null, requestId: null });

  const [formData, setFormData] = useState({
    // Step 1: Profile
    firstName: "",
    lastName: "",
    gender: "",
    birthDate: null,
    terms: false,

    // Step 2 (Corporate only): Company info
    companyName: "",
    companyAddress: "",
    companyRegistrationNumber: "",
    companyTaxNumber: "",
    accountNumber: "",

    // Step 3 (Personal) / Step 4 (Corporate): Contact & Identity
    email: "",
    phoneArea: "+234",
    phoneNo: "",
    addressLine1: "",
    addressLine2: "",
    city: "",
    state: "",
    postalCode: "",
    country: "Nigeria",
    biometricsId: "",
    idType: Object.values(MeansOfIdentificationEnum)[0]?.value,
    idNumber: "",

    // Final step: Security
    password: "",
    confirmPassword: "",
    selectedQuestions: [
      { question: "", answer: "" },
      { question: "", answer: "" },
      { question: "", answer: "" },
    ],
  });

  // OTP state
  const [otp, setOtp] = useState("");
  const [otpError, setOtpError] = useState(null);
  const [resendSeconds, setResendSeconds] = useState(59);
  const [isResendDisabled, setIsResendDisabled] = useState(true);

  // Security questions
  const [securityQuestions, setSecurityQuestions] = useState([]);
  const [isLoadingQuestions, setIsLoadingQuestions] = useState(false);

  // ──────────────────────────────────────────────────────────────────────────
  // Derived: step sequence based on account type
  // ──────────────────────────────────────────────────────────────────────────
  const isCorporate = accountType === "Corporate";

  // The wizard's step indicator only includes the data-entry steps.
  // OTP and Security are post-wizard stages, not steps.
  const stepDefs = useMemo(() => {
    const base = [
      { key: "type", title: "Account Type" },
      { key: "profile", title: "Profile" },
    ];
    if (isCorporate) base.push({ key: "company", title: "Company" });
    base.push({ key: "contact", title: "Contact & Identity" });
    return base;
  }, [isCorporate]);

  const currentStepKey = stepDefs[currentStep]?.key;
  const isFinalStep = currentStep === stepDefs.length - 1;

  // ──────────────────────────────────────────────────────────────────────────
  // Helpers
  // ──────────────────────────────────────────────────────────────────────────
  const handleAccountTypeSelect = (type) => {
    setAccountType(type);
    localStorage.setItem("accountType", type);
  };

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleQuestionChange = (index, value) => {
    const updated = [...formData.selectedQuestions];
    updated[index] = { ...updated[index], question: value };
    setFormData((prev) => ({ ...prev, selectedQuestions: updated }));
  };

  const handleAnswerChange = (index, value) => {
    const updated = [...formData.selectedQuestions];
    updated[index] = { ...updated[index], answer: value };
    setFormData((prev) => ({ ...prev, selectedQuestions: updated }));
  };

  const availableQuestions = (index) => {
    const selectedQuestionValues = formData.selectedQuestions.map(
      (q) => q.question
    );
    return securityQuestions.filter(
      (q) =>
        !selectedQuestionValues.includes(q.question) ||
        q.question === formData.selectedQuestions[index].question
    );
  };

  const getIdTypeDescription = (value) => {
    const selectedType = Object.values(MeansOfIdentificationEnum).find(
      (item) => item.value === value
    );
    return selectedType?.description || "ID";
  };

  // ──────────────────────────────────────────────────────────────────────────
  // Step-level field validation (validate only the visible fields per step)
  // ──────────────────────────────────────────────────────────────────────────
  const fieldsForStep = (key) => {
    switch (key) {
      case "profile":
        return ["firstName", "lastName", "gender", "birthDate", "terms"];
      case "company":
        return [
          "companyName",
          "companyAddress",
          "companyRegistrationNumber",
          "companyTaxNumber",
        ];
      case "contact":
        return [
          "email",
          "phoneNo",
          "addressLine1",
          "city",
          "state",
          "country",
          "biometricsId",
          "idType",
          "idNumber",
          ...(isCorporate ? ["accountNumber"] : []),
        ];
      case "security":
        return [
          "securityQuestion1",
          "answer1",
          "securityQuestion2",
          "answer2",
          "securityQuestion3",
          "answer3",
          "password",
          "confirmPassword",
        ];
      default:
        return [];
    }
  };

  // ──────────────────────────────────────────────────────────────────────────
  // API: pre-register (after Contact step)
  // ──────────────────────────────────────────────────────────────────────────
  const handlePreRegister = async () => {
    const sharedPayload = {
      firstName: formData.firstName,
      lastName: formData.lastName,
      emailAddress: formData.email,
      mobileCountryCode: formData.phoneArea,
      mobileNumber: formData.phoneNo,
      dateOfBirth: formData.birthDate
        ? formData.birthDate.toISOString?.() || formData.birthDate
        : null,
      gender: formData.gender,
      userType: accountType,
      biometricsId: formData.biometricsId,
      identityInformation: {
        idNumber: formData.idNumber,
        idType: formData.idType,
      },
      addressInformation: {
        addressLine1: formData.addressLine1,
        addressLine2: formData.addressLine2,
        state: formData.state,
        postalCode: formData.postalCode,
        city: formData.city,
        country: formData.country,
      },
    };

    setIsLoading(true);
    try {
      const response = isCorporate
        ? await preRegisterCorporateUser({
            ...sharedPayload,
            companyName: formData.companyName,
            companyRegistrationNumber: formData.companyRegistrationNumber,
            companyAddress: formData.companyAddress,
            companyTaxNumber: formData.companyTaxNumber,
            accountNumber: formData.accountNumber,
          })
        : await preRegisterUser(sharedPayload);

      const result = response?.outData;
      if (result?.isSuccessful) {
        // Store userId + requestId in BOTH the formData context (legacy consumers)
        // and a local session state used by the OTP / Security stages.
        setSignupContext(result.data);
        setSignupSession({
          userId: result.data?.userId,
          requestId: result.data?.requestId,
        });
        triggerNotification({
          type: "success",
          message: "Success",
          description: result?.message || "Registration started — verify your email",
        });
        // Reset OTP timer and transition to the standalone OTP stage
        setResendSeconds(59);
        setIsResendDisabled(true);
        setOtp("");
        setOtpError(null);
        setStage("otp");
      } else {
        triggerNotification({
          type: "error",
          message: "Pre-Registration Error",
          description:
            result?.message || "An error occurred. Please try again later.",
        });
      }
    } catch (error) {
      triggerNotification({
        type: "error",
        message: "Error",
        description:
          error?.message || "An error occurred. Please try again later.",
      });
    } finally {
      setIsLoading(false);
    }
  };

  // ──────────────────────────────────────────────────────────────────────────
  // API: OTP verify / resend
  // ──────────────────────────────────────────────────────────────────────────
  const handleVerifyOtp = async (codeOverride) => {
    const code = codeOverride ?? otp;
    if (!code || code.length !== 6) {
      setOtpError("Please enter the 6-digit OTP.");
      return;
    }
    const userId = signupSession?.userId || signupContext?.userId;
    const requestId = signupSession?.requestId || signupContext?.requestId;
    if (!userId || !requestId) {
      setOtpError("Missing session context. Please restart registration.");
      return;
    }
    setIsLoading(true);
    setOtpError(null);
    try {
      const response = await validateOTP({ userId, requestId, otp: code });
      const result = response?.outData;
      if (result?.isSuccessful) {
        triggerNotification({
          type: "success",
          message: "Success",
          description: result?.message || "OTP verified successfully",
        });
        // Transition to the standalone Security stage
        setStage("security");
      } else {
        setOtpError(result?.message || "Invalid OTP. Please try again.");
      }
    } catch (error) {
      setOtpError(error?.message || "Could not verify OTP. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleResendOtp = async () => {
    const userId = signupSession?.userId || signupContext?.userId;
    const requestId = signupSession?.requestId || signupContext?.requestId;
    if (!userId || !requestId) return;
    setOtpError(null);
    setOtp("");
    setResendSeconds(59);
    setIsResendDisabled(true);
    try {
      const response = await resendOTP({ userId, requestId });
      const result = response?.outData;
      if (result?.isSuccessful) {
        triggerNotification({
          type: "success",
          message: "Success",
          description: result?.message || "OTP has been resent successfully.",
        });
      } else {
        triggerNotification({
          type: "error",
          message: "Error",
          description: result?.message || "Could not resend OTP.",
        });
      }
    } catch (error) {
      triggerNotification({
        type: "error",
        message: "Error",
        description: error?.message || "Could not resend OTP.",
      });
    }
  };

  // OTP resend countdown — only ticks while the OTP stage is showing
  useEffect(() => {
    if (stage !== "otp") return;
    if (resendSeconds <= 0) {
      setIsResendDisabled(false);
      return;
    }
    const id = setInterval(
      () => setResendSeconds((s) => (s > 0 ? s - 1 : 0)),
      1000
    );
    return () => clearInterval(id);
  }, [resendSeconds, stage]);

  // ──────────────────────────────────────────────────────────────────────────
  // API: load security questions when entering the Security stage
  // ──────────────────────────────────────────────────────────────────────────
  useEffect(() => {
    if (stage !== "security" || securityQuestions.length > 0) return;
    const loadQuestions = async () => {
      setIsLoadingQuestions(true);
      try {
        const response = await securityQuestionQuery({ operation: "POST" });
        const result = response?.outData;
        if (result?.isSuccessful) {
          const transformed = (result?.data || []).map((item, idx) => ({
            id: idx + 1,
            question: item.question,
          }));
          setSecurityQuestions(transformed);
        }
      } catch (error) {
        triggerNotification({
          type: "error",
          message: "Error",
          description:
            error?.message || "Could not load security questions.",
        });
      } finally {
        setIsLoadingQuestions(false);
      }
    };
    loadQuestions();
  }, [stage]); // eslint-disable-line react-hooks/exhaustive-deps

  // ──────────────────────────────────────────────────────────────────────────
  // API: complete registration (final step submit)
  // ──────────────────────────────────────────────────────────────────────────
  const handleCompleteRegistration = async () => {
    const requestId = signupSession?.requestId || signupContext?.requestId;
    if (!requestId) {
      triggerNotification({
        type: "error",
        message: "Error",
        description: "Missing session context. Please restart registration.",
      });
      return;
    }
    // Validate security fields before submitting
    try {
      await form.validateFields([
        "securityQuestion1",
        "answer1",
        "securityQuestion2",
        "answer2",
        "securityQuestion3",
        "answer3",
        "password",
        "confirmPassword",
      ]);
    } catch {
      return;
    }
    setIsLoading(true);
    try {
      const response = await completeRegistration({
        requestId,
        password: formData.password,
        emailAddress: formData.email,
        securityQuestion1: formData.selectedQuestions[0].question,
        securityAnswer1: formData.selectedQuestions[0].answer,
        securityQuestion2: formData.selectedQuestions[1].question,
        securityAnswer2: formData.selectedQuestions[1].answer,
        securityQuestion3: formData.selectedQuestions[2].question,
        securityAnswer3: formData.selectedQuestions[2].answer,
      });
      const result = response?.outData;
      if (result?.isSuccessful) {
        triggerNotification({
          type: "success",
          message: "Success",
          description: result?.message || "Registration successful",
        });
        navigateToAuth({ isAdmin: false });
      } else {
        triggerNotification({
          type: "error",
          message: "Registration Error",
          description:
            result?.message ||
            "Could not complete registration. Please try again later.",
        });
      }
    } catch (error) {
      triggerNotification({
        type: "error",
        message: "Error",
        description:
          error?.message || "An error occurred. Please try again later.",
      });
    } finally {
      setIsLoading(false);
    }
  };

  // ──────────────────────────────────────────────────────────────────────────
  // Step navigation
  // ──────────────────────────────────────────────────────────────────────────
  const handleNext = async () => {
    if (currentStepKey === "type") {
      if (!accountType) {
        triggerNotification({
          type: "warning",
          message: "Select Account Type",
          description: "Please select an account type to continue.",
        });
        return;
      }
      setCurrentStep(currentStep + 1);
      return;
    }

    // Validate visible fields for this step
    try {
      const fields = fieldsForStep(currentStepKey);
      if (fields.length) {
        await form.validateFields(fields);
      }
    } catch {
      return; // validation message handled by antd
    }

    // Final wizard step (Contact & Identity) → Register → switch to OTP stage
    if (currentStepKey === "contact") {
      await handlePreRegister();
      return;
    }

    // Otherwise just advance to next wizard step
    setCurrentStep(currentStep + 1);
  };

  const handleBack = () => {
    if (currentStep > 0) setCurrentStep(currentStep - 1);
  };

  // ──────────────────────────────────────────────────────────────────────────
  // Form validators
  // ──────────────────────────────────────────────────────────────────────────
  const validatePassword = (_, value) => {
    if (
      /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@!£$#&*%])[A-Za-z\d@!£$#&*%]{8,}$/.test(
        value
      )
    ) {
      return Promise.resolve();
    }
    return Promise.reject(
      "Password must be at least 8 characters, with upper- and lower-case letters, a digit, and a special character (!,£,$,#,&,*,%)."
    );
  };

  const validateConfirmPassword = ({ getFieldValue }) => ({
    validator(_, value) {
      if (!value || getFieldValue("password") === value) {
        return Promise.resolve();
      }
      return Promise.reject("The two passwords do not match.");
    },
  });

  const validateBirthDate = (_, value) => {
    if (!value) return Promise.reject("Please select your date of birth.");
    const now = new Date();
    const dob = value.toDate ? value.toDate() : new Date(value);
    const age = (now - dob) / (365.25 * 24 * 60 * 60 * 1000);
    if (age < 18) return Promise.reject("You must be at least 18 years old.");
    return Promise.resolve();
  };

  // ──────────────────────────────────────────────────────────────────────────
  // Renderers
  // ──────────────────────────────────────────────────────────────────────────
  const labelStyle = { color: isDark ? "#ffffff" : "#000000" };

  const renderAccountType = () => (
    <div style={{ marginTop: "8px" }}>
      <div
        style={{
          textAlign: "center",
          marginBottom: "32px",
          padding: "20px",
          backgroundColor: isDark
            ? "rgba(24, 144, 255, 0.1)"
            : "rgba(24, 144, 255, 0.05)",
          borderRadius: "12px",
          border: `1px solid ${
            isDark ? "rgba(24, 144, 255, 0.2)" : "rgba(24, 144, 255, 0.1)"
          }`,
        }}
      >
        <Title
          level={4}
          style={{ color: isDark ? "#ffffff" : "#000000", marginBottom: "8px" }}
        >
          Let's get started! 🚀
        </Title>
        <Text style={{ color: isDark ? "#a0a0a0" : "#666666", fontSize: "14px" }}>
          Choose the account type that best suits your needs.
        </Text>
      </div>

      <Space direction="vertical" size="large" style={{ width: "100%" }}>
        {[
          {
            type: "Personal",
            icon: <UserOutlined style={{ fontSize: "28px", color: "#fff" }} />,
            title: "Personal Account",
            description: "Perfect for individuals managing personal finances",
            features: ["✓ Quick setup", "✓ Instant transfers", "✓ Bill payments"],
          },
          {
            type: "Corporate",
            icon: <BankOutlined style={{ fontSize: "28px", color: "#fff" }} />,
            title: "Corporate Account",
            description: "Designed for businesses, SMEs, and organizations",
            features: ["✓ Bulk payments", "✓ Team management", "✓ Advanced analytics"],
          },
        ].map(({ type, icon, title, description, features }) => {
          const selected = accountType === type;
          return (
            <div
              key={type}
              onClick={() => handleAccountTypeSelect(type)}
              style={{
                border: `2px solid ${
                  selected ? primary : isDark ? "#303030" : "#d9d9d9"
                }`,
                borderRadius: "12px",
                padding: "24px",
                cursor: "pointer",
                backgroundColor: selected
                  ? isDark
                    ? "rgba(24, 144, 255, 0.08)"
                    : "rgba(24, 144, 255, 0.05)"
                  : isDark
                  ? "rgba(0, 0, 0, 0.2)"
                  : "transparent",
                transition: "all 0.3s ease",
                boxShadow: selected ? `0 4px 12px ${primary}40` : "none",
              }}
            >
              <div style={{ display: "flex", alignItems: "center", gap: "16px" }}>
                <div
                  style={{
                    width: "56px",
                    height: "56px",
                    borderRadius: "12px",
                    backgroundColor: selected
                      ? primary
                      : isDark
                      ? "#303030"
                      : "#e8e8e8",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  {icon}
                </div>
                <div style={{ flex: 1 }}>
                  <Title
                    level={5}
                    style={{
                      margin: "0 0 6px 0",
                      color: isDark ? "#ffffff" : "#000000",
                    }}
                  >
                    {title}
                  </Title>
                  <Text
                    style={{
                      color: isDark ? "#a0a0a0" : "#666666",
                      fontSize: "14px",
                      display: "block",
                      marginBottom: "8px",
                    }}
                  >
                    {description}
                  </Text>
                  <div style={{ display: "flex", gap: "8px", flexWrap: "wrap" }}>
                    {features.map((f) => (
                      <Text
                        key={f}
                        style={{
                          fontSize: "12px",
                          color: primary,
                          backgroundColor: isDark
                            ? "rgba(24, 144, 255, 0.15)"
                            : "rgba(24, 144, 255, 0.1)",
                          padding: "2px 8px",
                          borderRadius: "4px",
                        }}
                      >
                        {f}
                      </Text>
                    ))}
                  </div>
                </div>
                <Radio checked={selected} />
              </div>
            </div>
          );
        })}
      </Space>
    </div>
  );

  const renderProfile = () => (
    <div style={{ marginTop: "16px" }}>
      <Form.Item
        label={<span style={labelStyle}>First Name</span>}
        name="firstName"
        rules={[{ required: true, message: "Please enter your first name." }]}
      >
        <Input
          prefix={<UserOutlined style={{ color: "#8c8c8c" }} />}
          placeholder="Enter your first name"
          size="large"
          value={formData.firstName}
          onChange={(e) => handleInputChange("firstName", e.target.value)}
        />
      </Form.Item>

      <Form.Item
        label={<span style={labelStyle}>Last Name</span>}
        name="lastName"
        rules={[{ required: true, message: "Please enter your last name." }]}
      >
        <Input
          prefix={<UserOutlined style={{ color: "#8c8c8c" }} />}
          placeholder="Enter your last name"
          size="large"
          value={formData.lastName}
          onChange={(e) => handleInputChange("lastName", e.target.value)}
        />
      </Form.Item>

      <Form.Item
        label={<span style={labelStyle}>Gender</span>}
        name="gender"
        rules={[{ required: true, message: "Please select a gender." }]}
      >
        <Select
          placeholder="Select gender"
          size="large"
          value={formData.gender}
          onChange={(value) => handleInputChange("gender", value)}
        >
          {GENDERS.map((g) => (
            <Option key={g} value={g}>
              {g}
            </Option>
          ))}
        </Select>
      </Form.Item>

      <Form.Item
        label={<span style={labelStyle}>Date of Birth</span>}
        name="birthDate"
        rules={[{ validator: validateBirthDate }]}
      >
        <DatePicker
          style={{ width: "100%" }}
          size="large"
          placeholder="Select date of birth"
          value={formData.birthDate}
          onChange={(value) => handleInputChange("birthDate", value)}
          disabledDate={(current) => current && current > new Date()}
        />
      </Form.Item>

      <Form.Item
        name="terms"
        valuePropName="checked"
        rules={[
          {
            validator: (_, v) =>
              v
                ? Promise.resolve()
                : Promise.reject("Please accept the terms and conditions."),
          },
        ]}
      >
        <Checkbox
          checked={formData.terms}
          onChange={(e) => handleInputChange("terms", e.target.checked)}
          style={{ color: isDark ? "#ffffff" : "#000000" }}
        >
          I accept the{" "}
          <Link style={{ color: primary }} onClick={() => window.open("/terms", "_blank")}>
            Terms
          </Link>{" "}
          and{" "}
          <Link style={{ color: primary }} onClick={() => window.open("/privacy", "_blank")}>
            Privacy Policy
          </Link>
          .
        </Checkbox>
      </Form.Item>
    </div>
  );

  const renderCompany = () => (
    <div style={{ marginTop: "16px" }}>
      <Form.Item
        label={<span style={labelStyle}>Company Name</span>}
        name="companyName"
        rules={[{ required: true, message: "Please enter the company name." }]}
      >
        <Input
          prefix={<ShopOutlined style={{ color: "#8c8c8c" }} />}
          placeholder="Enter the company name"
          size="large"
          value={formData.companyName}
          onChange={(e) => handleInputChange("companyName", e.target.value)}
        />
      </Form.Item>

      <Form.Item
        label={<span style={labelStyle}>Company Address</span>}
        name="companyAddress"
        rules={[{ required: true, message: "Please enter the company address." }]}
      >
        <Input
          placeholder="Enter the company address"
          size="large"
          value={formData.companyAddress}
          onChange={(e) => handleInputChange("companyAddress", e.target.value)}
        />
      </Form.Item>

      <Form.Item
        label={<span style={labelStyle}>Registration Number</span>}
        name="companyRegistrationNumber"
        rules={[
          { required: true, message: "Please enter the company registration number." },
        ]}
      >
        <Input
          placeholder="Enter the company registration number"
          size="large"
          value={formData.companyRegistrationNumber}
          onChange={(e) =>
            handleInputChange("companyRegistrationNumber", e.target.value)
          }
        />
      </Form.Item>

      <Form.Item
        label={<span style={labelStyle}>Tax Pin Number</span>}
        name="companyTaxNumber"
        rules={[{ required: true, message: "Please enter the tax pin number." }]}
      >
        <Input
          placeholder="Enter the tax pin number"
          size="large"
          value={formData.companyTaxNumber}
          onChange={(e) => handleInputChange("companyTaxNumber", e.target.value)}
        />
      </Form.Item>
    </div>
  );

  const renderContact = () => (
    <div style={{ marginTop: "16px" }}>
      <Form.Item
        label={<span style={labelStyle}>Email Address</span>}
        name="email"
        rules={[
          { required: true, message: "Please enter your email." },
          { type: "email", message: "Please enter a valid email." },
        ]}
      >
        <Input
          prefix={<MailOutlined style={{ color: "#8c8c8c" }} />}
          placeholder={
            isCorporate ? "Use your company email" : "Enter your email"
          }
          size="large"
          value={formData.email}
          onChange={(e) => handleInputChange("email", e.target.value)}
        />
      </Form.Item>

      <Form.Item
        label={<span style={labelStyle}>Mobile Number</span>}
        name="phoneNo"
        rules={[
          { required: true, message: "Please enter your phone number." },
          {
            validator: (_, v) => {
              if (!v) return Promise.resolve();
              const clean = String(v).replace(/\D/g, "");
              if (clean.startsWith("0") && clean.length !== 11)
                return Promise.reject(
                  "Phone numbers starting with 0 must be exactly 11 digits."
                );
              if (!clean.startsWith("0") && clean.length !== 10)
                return Promise.reject(
                  "Phone numbers without leading 0 must be exactly 10 digits."
                );
              return Promise.resolve();
            },
          },
        ]}
      >
        <Input
          size="large"
          maxLength={11}
          addonBefore={
            <Select
              style={{ width: 110 }}
              value={formData.phoneArea}
              onChange={(value) => handleInputChange("phoneArea", value)}
            >
              {PhoneAreas.map((item) => (
                <Option value={item.code} key={item.code}>
                  <span style={{ display: "inline-flex", alignItems: "center" }}>
                    <span>{item.code}</span>
                    {item.flag && (
                      <img
                        src={item.flag}
                        alt={item.country}
                        style={{ width: "18px", marginLeft: "6px" }}
                      />
                    )}
                  </span>
                </Option>
              ))}
            </Select>
          }
          placeholder="Enter your phone number"
          value={formData.phoneNo}
          onChange={(e) => handleInputChange("phoneNo", e.target.value)}
        />
      </Form.Item>

      <Form.Item
        label={<span style={labelStyle}>Address Line 1</span>}
        name="addressLine1"
        rules={[{ required: true, message: "Please enter your address." }]}
      >
        <Input
          placeholder="Enter address line 1"
          size="large"
          value={formData.addressLine1}
          onChange={(e) => handleInputChange("addressLine1", e.target.value)}
        />
      </Form.Item>

      <Form.Item
        label={<span style={labelStyle}>Address Line 2 (optional)</span>}
        name="addressLine2"
      >
        <Input
          placeholder="Enter address line 2"
          size="large"
          value={formData.addressLine2}
          onChange={(e) => handleInputChange("addressLine2", e.target.value)}
        />
      </Form.Item>

      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "12px" }}>
        <Form.Item
          label={<span style={labelStyle}>City</span>}
          name="city"
          rules={[{ required: true, message: "Please enter your city." }]}
        >
          <Input
            placeholder="City"
            size="large"
            value={formData.city}
            onChange={(e) => handleInputChange("city", e.target.value)}
          />
        </Form.Item>
        <Form.Item
          label={<span style={labelStyle}>State</span>}
          name="state"
          rules={[{ required: true, message: "Please enter your state." }]}
        >
          <Input
            placeholder="State"
            size="large"
            value={formData.state}
            onChange={(e) => handleInputChange("state", e.target.value)}
          />
        </Form.Item>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "12px" }}>
        <Form.Item
          label={<span style={labelStyle}>Postal Code (optional)</span>}
          name="postalCode"
        >
          <Input
            placeholder="Postal code"
            size="large"
            value={formData.postalCode}
            onChange={(e) => handleInputChange("postalCode", e.target.value)}
          />
        </Form.Item>
        <Form.Item
          label={<span style={labelStyle}>Country</span>}
          name="country"
          rules={[{ required: true, message: "Please select your country." }]}
        >
          <Select
            placeholder="Country"
            size="large"
            value={formData.country}
            onChange={(value) => handleInputChange("country", value)}
            showSearch
          >
            {[...new Set(PhoneAreas.map((a) => a.country))]
              .sort()
              .map((c) => (
                <Option key={c} value={c}>
                  {c}
                </Option>
              ))}
          </Select>
        </Form.Item>
      </div>

      <Form.Item
        label={<span style={labelStyle}>Biometrics ID (BVN)</span>}
        name="biometricsId"
        rules={[
          { required: true, message: "Please enter your BVN." },
          { pattern: /^\d{11}$/, message: "BVN must be exactly 11 digits." },
        ]}
      >
        <Input
          prefix={<IdcardOutlined style={{ color: "#8c8c8c" }} />}
          placeholder="Enter your 11-digit BVN"
          size="large"
          maxLength={11}
          value={formData.biometricsId}
          onChange={(e) => handleInputChange("biometricsId", e.target.value)}
        />
      </Form.Item>

      {isCorporate && (
        <Form.Item
          label={<span style={labelStyle}>Corporate Account Number</span>}
          name="accountNumber"
          rules={[{ required: true, message: "Please enter the account number." }]}
        >
          <InputNumber
            placeholder="Enter account number"
            size="large"
            style={{ width: "100%" }}
            controls={false}
            value={formData.accountNumber}
            onChange={(value) => handleInputChange("accountNumber", value)}
          />
        </Form.Item>
      )}

      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "12px" }}>
        <Form.Item
          label={<span style={labelStyle}>Means of Identification</span>}
          name="idType"
          rules={[{ required: true, message: "Please select an ID type." }]}
        >
          <Select
            placeholder="Select an ID type"
            size="large"
            value={formData.idType}
            onChange={(value) => handleInputChange("idType", value)}
          >
            {Object.values(MeansOfIdentificationEnum).map((item) => (
              <Option key={item.value} value={item.value}>
                {item.description}
              </Option>
            ))}
          </Select>
        </Form.Item>
        <Form.Item
          label={
            <span style={labelStyle}>
              {getIdTypeDescription(formData.idType)} Number
            </span>
          }
          name="idNumber"
          rules={[{ required: true, message: "Please enter your ID number." }]}
        >
          <Input
            placeholder="Enter ID number"
            size="large"
            value={formData.idNumber}
            onChange={(e) => handleInputChange("idNumber", e.target.value)}
          />
        </Form.Item>
      </div>
    </div>
  );

  // ── OTP stage — visually mirrors the login OTP screen (OTPNew.js) ─────────
  const renderOtpStage = () => {
    const maskedEmail =
      formData.email && formData.email.length > 4
        ? formData.email.replace(/(.{2})(.*)(@.*)/, "$1***$3")
        : "your email";

    return (
      <div style={{ textAlign: "center" }}>
        {/* Safety icon in a circular tinted background — same as login */}
        <div
          style={{
            width: "64px",
            height: "64px",
            margin: "0 auto 20px",
            borderRadius: "50%",
            backgroundColor: `${primary}20`,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <SafetyOutlined style={{ fontSize: "32px", color: primary }} />
        </div>

        <Title
          level={3}
          style={{
            color: isDark ? "#fff" : "#000",
            fontSize: "24px",
            fontWeight: 600,
            marginBottom: "10px",
          }}
        >
          Verify Your Identity
        </Title>
        <Text
          style={{
            display: "block",
            color: isDark ? "#a0a0a0" : "#595959",
            fontSize: "14px",
            marginBottom: "28px",
            lineHeight: "1.6",
          }}
        >
          We've sent a 6-digit verification code to your email ({maskedEmail})
        </Text>

        {/* OTP inputs — 56x56 boxes with 2px primary border when filled, same as login */}
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            marginBottom: "24px",
          }}
        >
          <OtpInput
            value={otp}
            onChange={(v) => {
              setOtp(v);
              if (v.length === 6) handleVerifyOtp(v);
            }}
            numInputs={6}
            renderSeparator={<span style={{ width: "8px" }} />}
            renderInput={(props, idx) => (
              <input
                {...props}
                inputMode="numeric"
                style={{
                  width: "52px",
                  height: "56px",
                  margin: "0 4px",
                  fontSize: "24px",
                  fontWeight: 600,
                  textAlign: "center",
                  borderRadius: "8px",
                  border: `2px solid ${
                    otp[idx]
                      ? primary
                      : isDark
                      ? "rgba(255,255,255,0.2)"
                      : "rgba(0,0,0,0.15)"
                  }`,
                  backgroundColor: isDark ? "#1f1f1f" : "#fff",
                  color: isDark ? "#fff" : "#000",
                  outline: "none",
                }}
              />
            )}
          />
        </div>

        {otpError && (
          <Text type="danger" style={{ display: "block", marginBottom: "12px" }}>
            {otpError}
          </Text>
        )}

        <Button
          type="primary"
          size="large"
          onClick={() => handleVerifyOtp()}
          loading={isLoading}
          disabled={otp.length !== 6}
          style={{
            width: "100%",
            height: "48px",
            fontSize: "16px",
            fontWeight: 600,
            backgroundColor: primary,
            borderColor: primary,
            marginBottom: "12px",
          }}
        >
          Verify & Continue
        </Button>

        <div>
          {isResendDisabled ? (
            <Text style={{ color: isDark ? "#a0a0a0" : "#595959" }}>
              Resend code in {resendSeconds < 10 ? `0${resendSeconds}` : resendSeconds}s
            </Text>
          ) : (
            <Link onClick={handleResendOtp} style={{ color: primary }}>
              Resend Code
            </Link>
          )}
        </div>
      </div>
    );
  };

  // ── Security stage — isolated screen, matching the wizard's design language ─
  const renderSecurityStage = () => (
    <div>
      <div style={{ textAlign: "center", marginBottom: "24px" }}>
        <Title
          level={3}
          style={{
            color: isDark ? "#fff" : "#000",
            fontSize: "22px",
            fontWeight: 600,
            marginBottom: "6px",
          }}
        >
          Set up your security
        </Title>
        <Text style={{ color: isDark ? "#a0a0a0" : "#595959", fontSize: "14px" }}>
          Choose three questions and a strong password to protect your account
        </Text>
      </div>

      {isLoadingQuestions && (
        <Text style={{ color: primary, display: "block", marginBottom: "12px" }}>
          Fetching security questions…
        </Text>
      )}

      <Form form={form} layout="vertical" requiredMark={false}>
      {formData.selectedQuestions.map((item, index) => (
        <div key={index}>
          <Form.Item
            label={<span style={labelStyle}>{`Security Question ${index + 1}`}</span>}
            name={`securityQuestion${index + 1}`}
            rules={[
              { required: true, message: `Please select Security Question ${index + 1}.` },
            ]}
          >
            <Select
              placeholder={`Select Security Question ${index + 1}`}
              size="large"
              value={item.question}
              onChange={(value) => handleQuestionChange(index, value)}
            >
              {availableQuestions(index).map((q) => (
                <Option key={q.id} value={q.question}>
                  {q.question}
                </Option>
              ))}
            </Select>
          </Form.Item>
          <Form.Item
            label={<span style={labelStyle}>{`Answer ${index + 1}`}</span>}
            name={`answer${index + 1}`}
            rules={[
              { required: true, message: `Please provide an answer for Question ${index + 1}.` },
            ]}
          >
            <Input
              placeholder={`Answer for Question ${index + 1}`}
              size="large"
              value={item.answer}
              onChange={(e) => handleAnswerChange(index, e.target.value)}
            />
          </Form.Item>
        </div>
      ))}

      <Form.Item
        label={<span style={labelStyle}>Password</span>}
        name="password"
        rules={[
          { required: true, message: "Please enter your password." },
          { validator: validatePassword },
        ]}
      >
        <Input.Password
          prefix={<LockOutlined style={{ color: "#8c8c8c" }} />}
          placeholder="Create a password"
          size="large"
          value={formData.password}
          onChange={(e) => handleInputChange("password", e.target.value)}
          iconRender={(visible) =>
            visible ? <EyeOutlined /> : <EyeInvisibleOutlined />
          }
        />
      </Form.Item>
      <Form.Item
        label={<span style={labelStyle}>Confirm Password</span>}
        name="confirmPassword"
        dependencies={["password"]}
        rules={[
          { required: true, message: "Please confirm your password." },
          validateConfirmPassword,
        ]}
      >
        <Input.Password
          prefix={<LockOutlined style={{ color: "#8c8c8c" }} />}
          placeholder="Confirm your password"
          size="large"
          value={formData.confirmPassword}
          onChange={(e) => handleInputChange("confirmPassword", e.target.value)}
          iconRender={(visible) =>
            visible ? <EyeOutlined /> : <EyeInvisibleOutlined />
          }
        />
      </Form.Item>
      </Form>

      <Button
        type="primary"
        size="large"
        onClick={handleCompleteRegistration}
        loading={isLoading}
        style={{
          width: "100%",
          height: "48px",
          marginTop: "8px",
          fontSize: "16px",
          fontWeight: 600,
          backgroundColor: primary,
          borderColor: primary,
        }}
      >
        Create Account
      </Button>
    </div>
  );

  const stepContent = () => {
    switch (currentStepKey) {
      case "type":
        return renderAccountType();
      case "profile":
        return renderProfile();
      case "company":
        return renderCompany();
      case "contact":
        return renderContact();
      default:
        return null;
    }
  };

  const nextLabel = () => {
    if (currentStepKey === "contact") return "Register";
    return "Continue";
  };

  // ──────────────────────────────────────────────────────────────────────────
  // Frame
  // ──────────────────────────────────────────────────────────────────────────
  return (
    <div
      style={{
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        position: "relative",
        overflow: "hidden",
        background: isDark
          ? `linear-gradient(135deg, #0f0c29 0%, ${primary}15 50%, #24243e 100%)`
          : `linear-gradient(135deg, ${primary}20 0%, ${primary}40 50%, ${primary}30 100%)`,
        padding: "20px",
      }}
    >
      <style>
        {`
          @keyframes floatSignup {
            0%, 100% { transform: translate(0, 0) rotate(0deg); }
            33% { transform: translate(30px, -30px) rotate(120deg); }
            66% { transform: translate(-20px, 20px) rotate(240deg); }
          }
          @keyframes pulseSignup {
            0%, 100% { opacity: 0.3; transform: scale(1); }
            50% { opacity: 0.5; transform: scale(1.15); }
          }
          @keyframes driftSignup {
            0% { transform: translate(0, 0); }
            25% { transform: translate(20px, -20px); }
            50% { transform: translate(40px, 10px); }
            75% { transform: translate(20px, 30px); }
            100% { transform: translate(0, 0); }
          }
          @keyframes gradientWaveSignup {
            0% { transform: translateX(-50%) translateY(-50%); }
            50% { transform: translateX(-40%) translateY(-60%); }
            100% { transform: translateX(-50%) translateY(-50%); }
          }
          .signup-floating-shape {
            position: absolute;
            border-radius: 50%;
            filter: blur(60px);
            opacity: 0.4;
          }
          .signup-shape-1 {
            width: 400px; height: 400px;
            background: ${isDark ? "rgba(255,255,255,0.08)" : `${primary}30`};
            top: -100px; right: -100px;
            animation: floatSignup 25s ease-in-out infinite;
          }
          .signup-shape-2 {
            width: 300px; height: 300px;
            background: ${isDark ? "rgba(255,255,255,0.05)" : `${primary}25`};
            bottom: -80px; left: -80px;
            animation: floatSignup 20s ease-in-out infinite reverse;
          }
          .signup-shape-3 {
            width: 250px; height: 250px;
            background: ${isDark ? "rgba(255,255,255,0.06)" : `${primary}20`};
            top: 50%; left: 20%;
            animation: pulseSignup 15s ease-in-out infinite;
          }
          .signup-particle {
            position: absolute;
            width: 3px; height: 3px;
            background: ${isDark ? "rgba(255,255,255,0.4)" : primary};
            border-radius: 50%;
            box-shadow: 0 0 8px ${isDark ? "rgba(255,255,255,0.3)" : `${primary}80`};
          }
        `}
      </style>

      <div
        style={{
          position: "absolute",
          inset: 0,
          background: `
            radial-gradient(circle at 20% 50%, ${primary}15 0%, transparent 50%),
            radial-gradient(circle at 80% 80%, ${primary}10 0%, transparent 50%),
            radial-gradient(circle at 40% 20%, ${primary}12 0%, transparent 50%)
          `,
          animation: "gradientWaveSignup 20s ease infinite",
        }}
      />

      <div className="signup-floating-shape signup-shape-1" />
      <div className="signup-floating-shape signup-shape-2" />
      <div className="signup-floating-shape signup-shape-3" />

      {[...Array(12)].map((_, i) => (
        <div
          key={i}
          className="signup-particle"
          style={{
            top: `${(i * 37) % 100}%`,
            left: `${(i * 53) % 100}%`,
            animation: `driftSignup ${12 + (i % 8)}s ease-in-out infinite`,
            animationDelay: `${i * 0.3}s`,
            opacity: 0.2 + (i % 3) * 0.1,
          }}
        />
      ))}

      <div
        style={{
          position: "absolute",
          top: "20px",
          right: "20px",
          zIndex: 10,
          display: "flex",
          gap: "12px",
        }}
      >
        {process.env.NODE_ENV === "development" &&
          (window.location.hostname === "localhost" ||
            window.location.hostname === "127.0.0.1") && <TenantSwitcher />}
        <LanguageSelector />
      </div>

      <Button
        type="text"
        icon={<ArrowLeftOutlined />}
        onClick={() => navigate("/landing")}
        style={{
          position: "absolute",
          top: "20px",
          left: "20px",
          color: isDark ? "#ffffff" : "#666666",
          zIndex: 10,
          fontWeight: 500,
        }}
      >
        Back to Home
      </Button>

      <div style={{ width: "100%", maxWidth: "640px", zIndex: 1 }}>
        <div
          style={{
            backgroundColor: isDark
              ? "rgba(31, 31, 31, 0.95)"
              : "rgba(255, 255, 255, 0.98)",
            borderRadius: "20px",
            padding: "48px 40px",
            boxShadow: isDark
              ? "0 30px 80px rgba(0,0,0,0.5)"
              : "0 30px 80px rgba(0,0,0,0.2)",
            backdropFilter: "blur(10px)",
          }}
        >
          <div style={{ textAlign: "center", marginBottom: "28px" }}>
            <img
              src={isDark ? tenant.darkLogo : tenant.logo}
              alt={tenant.name}
              style={{
                maxWidth: "90px",
                height: "auto",
                marginBottom: "16px",
                display: "block",
                marginLeft: "auto",
                marginRight: "auto",
              }}
            />
            {stage === "wizard" && (
              <>
                <Title
                  level={2}
                  style={{
                    margin: "0 0 6px 0",
                    color: isDark ? "#ffffff" : "#000000",
                    fontSize: "26px",
                    fontWeight: 600,
                  }}
                >
                  Create Your Account
                </Title>
                <Text style={{ color: isDark ? "#a0a0a0" : "#666666", fontSize: "14px" }}>
                  Join thousands of customers banking with {tenant?.name || "us"}
                </Text>
              </>
            )}
          </div>

          {stage === "wizard" && (
            <>
              <Steps
                current={currentStep}
                size="small"
                responsive
                style={{ marginBottom: "28px" }}
              >
                {stepDefs.map((s) => (
                  <Step key={s.key} title={s.title} />
                ))}
              </Steps>

              <Form
                form={form}
                layout="vertical"
                requiredMark={false}
                initialValues={{
                  phoneArea: formData.phoneArea,
                  country: formData.country,
                  idType: formData.idType,
                }}
              >
                {stepContent()}
              </Form>

              <div
                style={{
                  display: "flex",
                  gap: "12px",
                  marginTop: "24px",
                }}
              >
                {currentStep > 0 && (
                  <Button
                    size="large"
                    onClick={handleBack}
                    disabled={isLoading}
                    style={{ flex: 1, height: "48px", fontSize: "15px" }}
                  >
                    Back
                  </Button>
                )}
                <Button
                  type="primary"
                  size="large"
                  onClick={handleNext}
                  loading={isLoading}
                  disabled={currentStepKey === "type" && !accountType}
                  style={{
                    flex: 2,
                    backgroundColor: primary,
                    borderColor: primary,
                    height: "48px",
                    fontSize: "15px",
                    fontWeight: 500,
                  }}
                >
                  {nextLabel()}
                </Button>
              </div>
            </>
          )}

          {stage === "otp" && renderOtpStage()}
          {stage === "security" && renderSecurityStage()}

          <div style={{ textAlign: "center", marginTop: "20px" }}>
            <Text style={{ color: isDark ? "#8c8c8c" : "#595959" }}>
              Already have an account?{" "}
              <Link
                onClick={() => navigate("/authentication/login")}
                style={{ color: primary, fontWeight: 500 }}
              >
                Sign in
              </Link>
            </Text>
          </div>

          <div
            style={{
              textAlign: "center",
              marginTop: "24px",
              paddingTop: "20px",
              borderTop: `1px solid ${isDark ? "#303030" : "#f0f0f0"}`,
            }}
          >
            <Text style={{ color: "#8c8c8c", fontSize: "12px" }}>
              {tenant?.name} • Powered by <span style={{ fontWeight: 600 }}>BankLingo</span>
            </Text>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Signup;
